import { wishlistsGet, wishlistCreate, wishlistItemAdd, wishlistItemRemove, } from "./wishlist";
import { authenticationOpen, productOpen, cartAdd, collectionOpen, goBack, } from "./shared";
import { screenOpen } from "./screenOpen";
import { showToast } from "./toast";
import { vaultInitialize, vaultGet, vaultSet, vaultRemove, vaultClear, vaultKeys, vaultObserve, } from "./vault";
const todo = (actionName) => (_data, handlers) => {
    var _a;
    console.warn(`[storefront-bridge] action "${actionName}" called but not yet implemented in web mode`);
    (_a = handlers.onSuccess) === null || _a === void 0 ? void 0 : _a.call(handlers, {});
};
const noop = (_data, handlers) => {
    var _a;
    (_a = handlers.onSuccess) === null || _a === void 0 ? void 0 : _a.call(handlers, {});
};
// customer/identity — return the bootstrapped identity. The value flows
// from the verified Shopify session (App Proxy identity bootstrap or
// server-injected config), never from block input.
const customerIdentity = (_data, handlers, ctx) => {
    var _a, _b, _c, _d, _e;
    const customer = (_b = (_a = ctx.variables) === null || _a === void 0 ? void 0 : _a.variables) === null || _b === void 0 ? void 0 : _b.customer;
    (_c = handlers.onSuccess) === null || _c === void 0 ? void 0 : _c.call(handlers, {
        customerId: (_d = customer === null || customer === void 0 ? void 0 : customer.id) !== null && _d !== void 0 ? _d : null,
        email: (_e = customer === null || customer === void 0 ? void 0 : customer.email) !== null && _e !== void 0 ? _e : null,
    });
};
const ACTION_REGISTRY = {
    // Wishlist — localStorage-backed for v0; App Proxy backend per task #5.
    "wishlist/create": wishlistCreate,
    "wishlists/get": wishlistsGet,
    "wishlist/item/add": wishlistItemAdd,
    "wishlist/item/remove": wishlistItemRemove,
    // Cart — Shopify AJAX cart (top-level browser context). cart/add is real;
    // cart/open / cart/remove / cart attribute mutations land alongside the
    // Theme App Extension iframe-aware overrides in task #7.
    "cart/add": cartAdd,
    "cart/open": todo("cart/open"),
    "cart/remove": todo("cart/remove"),
    "cart/updateAttributes": todo("cart/updateAttributes"),
    "cart/updateNote": todo("cart/updateNote"),
    // Navigation — window.location (top-level). Iframe-mode overrides via
    // registerActionImpl in task #7.
    // screen/open resolves the destination to a same-origin web route
    // (/[appId]/web/<pageId|screenType>) and navigates the iframe. The SSR
    // web route resolves a screenType segment (e.g. "wishlist-detail") to the
    // app's published page server-side. See actions/screenOpen.ts.
    "screen/open": screenOpen,
    "product/open": productOpen,
    "collection/open": collectionOpen,
    "go/back": goBack,
    "view/scrollToBlockTop": todo("view/scrollToBlockTop"),
    "view/scrollToBlockBottom": todo("view/scrollToBlockBottom"),
    // Identity — Shopify storefront /account/login (works for Classic + New).
    "authentication/open": authenticationOpen,
    "customer/identity": customerIdentity,
    // Discounts / gift cards — Shopify AJAX cart attributes pending
    "discount/apply": todo("discount/apply"),
    "discount/remove": todo("discount/remove"),
    "discount/remove/all": todo("discount/remove/all"),
    "giftcard/apply": todo("giftcard/apply"),
    // Analytics — passthrough to web tracker eventually
    track: todo("track"),
    "analytics/track": todo("analytics/track"),
    "view/rendered": todo("view/rendered"),
    "view/updated": todo("view/updated"),
    // Toast is a real DOM toast (actions/toast.ts); share pending
    "app/toast": showToast,
    "app/share": todo("app/share"),
    // Vault — real, localStorage-backed (actions/vault.ts); observe is one-shot
    "vault/initialize": vaultInitialize,
    "vault/get": vaultGet,
    "vault/set": vaultSet,
    "vault/remove": vaultRemove,
    "vault/clear": vaultClear,
    "vault/keys": vaultKeys,
    "vault/observe": vaultObserve,
    // Genuinely no-op on web — haptics don't exist, push is its own product
    "trigger/haptic": noop,
    "enable/push": noop,
    // Gift cards — Shopify AJAX equivalents pending
    "giftcard/get": todo("giftcard/get"),
    "giftcard/remove": todo("giftcard/remove"),
    // Loyalty — backed by loyalty-core integrations on mobile; web pending
    "loyalty/rewards": todo("loyalty/rewards"),
    "loyalty/history": todo("loyalty/history"),
    "loyalty/customer": todo("loyalty/customer"),
    "loyalty/tiers": todo("loyalty/tiers"),
    "loyalty/customer/tier": todo("loyalty/customer/tier"),
    "loyalty/redeem/reward": todo("loyalty/redeem/reward"),
    "loyalty/record/activity": todo("loyalty/record/activity"),
    "loyalty/set/birthday": todo("loyalty/set/birthday"),
    "loyalty/accept/legal": todo("loyalty/accept/legal"),
    // Apple Pay — native-shell-only by definition; stubs exist so block code
    // probing these never TypeErrors. A web checkout never initiates them.
    "cart/applePayStartSessionURL": todo("cart/applePayStartSessionURL"),
    "cart/getApplePayDomainName": todo("cart/getApplePayDomainName"),
    "cart/applePayOnPaymentAuthorized": todo("cart/applePayOnPaymentAuthorized"),
    "cart/applePayOnShippingContactSelected": todo("cart/applePayOnShippingContactSelected"),
    "cart/applePayOnPaymentMethodSelected": todo("cart/applePayOnPaymentMethodSelected"),
    "cart/applePayOnShippingMethodSelected": todo("cart/applePayOnShippingMethodSelected"),
};
/**
 * Action names with a registered implementation (real or stub). Exported
 * for the SDK parity check (`scripts/check-sdk-parity.mjs`), which asserts
 * every action in the installed @tapcart/app-studio-sdk contract has an
 * entry here — so SDK additions fail CI instead of TypeError'ing in a
 * merchant's storefront.
 */
export const registeredActionNames = Object.keys(ACTION_REGISTRY);
export const createActionDispatcher = (ctx) => {
    return (actionType, data, handlers = {}) => {
        var _a, _b;
        const impl = ACTION_REGISTRY[actionType];
        if (!impl) {
            console.warn(`[storefront-bridge] unknown action "${actionType}"`);
            (_a = handlers.onError) === null || _a === void 0 ? void 0 : _a.call(handlers, new Error(`Unknown action: ${actionType}`));
            return;
        }
        try {
            impl(data, handlers, ctx);
        }
        catch (err) {
            console.error(`[storefront-bridge] action "${actionType}" threw`, err);
            (_b = handlers.onError) === null || _b === void 0 ? void 0 : _b.call(handlers, err);
        }
    };
};
/**
 * Replace a stub at runtime. Used by the consuming app to inject real
 * implementations (e.g. wishlist actions backed by the App Proxy fetch
 * client built from request-scoped credentials).
 */
export const registerActionImpl = (actionName, impl) => {
    ACTION_REGISTRY[actionName] = impl;
};
